import { createContext, useEffect, useState, useRef } from "react";
import { useGetConfig, useGetContext } from "@onefront/forrestjs-react-root";
import { useServiceLogin } from "./use-service-login";

const USE_APPLICATION_TOKEN = "one.auth.strategy.useApplicationToken.hook";

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const startTime = useRef(new Date());

  // The application token offers a strategy for retrieval and refresh
  // the strategy itself is replaceable via hooks
  const useApplicationToken = useGetContext(USE_APPLICATION_TOKEN);
  const applicationTokenInstance = useApplicationToken();

  // Internal state:
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [token, setToken] = useState(null);
  const [tokenData, setTokenData] = useState(null);
  const [sessionData, setSessionData] = useState(null);

  // Feature flags:
  const verifyToken = useGetConfig("one.auth.token.verify", false);
  const refreshToken = useGetConfig("one.auth.token.refresh", false);
  const keepAlive = Number(useGetConfig("one.auth.token.keepAlive", 0));

  // Token verification and refresh promises:
  const refreshPromise = useRef(null);
  const verifyPromise = useRef(null);

  const loadingDelay = useGetConfig("one.loading.delay", 0);

  // Logout handlers
  const logoutHandlers = useGetContext("one.auth.logout.handlers");
  const { logoutUrl } = useServiceLogin();

  const _refreshToken = () => {
    if (!refreshPromise.current) {
      refreshPromise.current = new Promise((resolve, reject) => {
        applicationTokenInstance
          .refresh(token)
          .then((data) => {
            refreshPromise.current = null;
            setToken(data.token);
            setTokenData(data.tokenData);
            resolve(data);
          })
          .catch((err) => {
            console.warn(
              "[AuthProvider] Failed token refresh, force client logout"
            );
            refreshPromise.current = null;
            applicationTokenInstance.set(null);
            setError(err);
            setLoading(false);
            reject(err);
          });
      });
    }
    return refreshPromise.current;
  };

  const _verifyToken = (receivedToken = token) => {
    if (!verifyPromise.current) {
      verifyPromise.current = new Promise((resolve, reject) => {
        applicationTokenInstance
          .verify(receivedToken)
          .then((data) => {
            verifyPromise.current = null;
            setTokenData(data.tokenData);
            resolve(data);
          })
          .catch((err) => {
            console.warn(
              "[AuthProvider] Failed token verification, force client logout"
            );
            verifyPromise.current = null;
            applicationTokenInstance.set(null);
            setError(err);
            setLoading(false);
            reject(err);
          });
      });
    }
    return verifyPromise.current;
  };

  const _setToken = async (token) => {
    try {
      const tokenData = await applicationTokenInstance.verify(token);
      await applicationTokenInstance.set(token);
      setToken(token);
      setTokenData(tokenData);
    } catch (err) {
      setError(err);
    }
  };

  const _dropToken = async () => {
    try {
      await applicationTokenInstance.set(null);
      setToken(null);
      setTokenData(null);
      setSessionData(null);
    } catch (err) {
      console.error(`Unable to delete the token: ${err.message}`);
    }
  };

  const _logout = async () => {
    _dropToken();

    await Promise.all(
      logoutHandlers.map(async (handler) => {
        try {
          await handler();
        } catch (err) {
          console.error(`Logout handler error: ${err.message}`);
        }
      })
    );

    // Redirect to the logout URL
    window.location.href = logoutUrl();
  };

  // Grant access to the App
  // (boot time Access Token lifecycle)
  useEffect(() => {
    (async () => {
      let token = null;

      const flags = { verifyToken, refreshToken };
      // console.log("@flags", flags);

      // Retrieve Token
      try {
        const { applicationToken, state } =
          await applicationTokenInstance.get();
        token = applicationToken;

        // Login required scenario
        if (!token) {
          setLoading(false);
          return;
        }

        setToken(token);
        if (state) {
          setSessionData(state);
        }
      } catch (err) {
        setError(err);
        setLoading(false);
        return;
      }

      // Introspect Token
      try {
        const { tokenData } = await applicationTokenInstance.introspect(token);
        setTokenData(tokenData);
      } catch (err) {
        console.warn("Failed token introspection, force verification", token);
        flags.verifyToken = false;
        flags.refreshToken = true;
      }

      // Verify Token
      if (flags.verifyToken) {
        try {
          await _verifyToken(token);
        } catch (err) {
          console.warn("Failed token verification, force refresh", err.message);
          flags.refreshToken = true;
        }
      }

      // Refresh Token
      if (flags.refreshToken) {
        // console.log("@refresh token");
        try {
          await _refreshToken();
        } catch (err) {
          // 400 404 must not happen
          //console.log("ERRORE QUI", err.message);
        }
      }

      // Start background refresh
      if (keepAlive > 0) {
        let ticker = null;

        const loop = async () => {
          clearTimeout(ticker);

          try {
            const data = await applicationTokenInstance.refresh(token);
            setToken(data.token);
            setTokenData(data.tokenData);
          } catch (err) {
            setToken(null);
            setError(err);
            setLoading(false);
            return;
          }

          ticker = setTimeout(loop, keepAlive);
        };

        // Kick the ticker
        ticker = setTimeout(loop, keepAlive);
      }

      setLoading(false);
    })();
  }, []); // eslint-disable-line

  // Loading Logo
  useEffect(() => {
    // skip if already removed
    const loadingElement = document.getElementById("loading-logo");
    if (!loadingElement) return;

    // Setup the fadeout effect
    loadingElement.style.opacity = 1;
    loadingElement.style.transition = `opacity 0.2s ease-in-out`;

    // Wait at least X seconds
    const delay = loadingDelay - (new Date() - startTime.current);
    let waitInterval = setTimeout(() => {
      loadingElement.style.opacity = 0;

      waitInterval = setTimeout(
        loadingElement.remove.bind(loadingElement),
        300
      );
    }, delay);

    return () => clearTimeout(waitInterval);
  }, [loading]);

  return (
    <AuthContext.Provider
      value={{
        loading,
        error,
        isPublic: token === null,
        token,
        tokenData,
        sessionData,
        logout: _logout,
        setToken: _setToken,
        dropToken: _dropToken,
        refreshToken: _refreshToken,
        verifyToken: _verifyToken
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
